import {useState} from 'react';
import {AnimatePresence, motion} from 'framer-motion';
import moment from 'moment';
import {headlineMoney, sizeClass} from './sectionMeta';
import {settlementWait} from './MetricsSections';
import styles from './ordersMetrics.module.scss';

/**
 * The top of the metrics tab.
 *
 * There was no reading order here: the server's seven scope notes opened the
 * page as a wall of grey prose, the one headline figure sat small beside them,
 * and then seven sections each offered two more numbers of equal weight. Every
 * figure looked as important as every other, so none of them led.
 *
 * This establishes one: a single primary count, four supporting figures an
 * operator actually opens this tab to check, and the notes folded away behind
 * a disclosure that says how many there are. The notes still matter — they are
 * the server's own caveats — but they are reference, not the headline.
 */

const number = value =>
  value == null ? '—' : new Intl.NumberFormat().format(value);

const DATE_FIELD_LABELS = {
  CREATED_AT: 'created',
  PLACED_AT: 'placed',
  LAST_FILL_DATE: 'last filled',
  SETTLEMENT_DATE: 'settled',
};

/** One supporting figure. `tone` marks the ones that need attention. */
const Kpi = ({label, value, currency, extra, hint, tone}) => (
  <div className={`${styles.kpi} ${tone ? styles[`kpi${tone}`] : ''}`}>
    <span className={styles.kpiLabel}>{label}</span>
    <span
      className={`${styles.kpiValue} ${
        styles[`kpiValue${sizeClass(value)}`] ?? ''
      }`}>
      {currency && <span className={styles.kpiCurrency}>{currency}</span>}
      {value}
      {extra && <span className={styles.kpiExtra}>{extra}</span>}
    </span>
    {hint && <span className={styles.kpiHint}>{hint}</span>}
  </div>
);

export const MetricsHero = ({metrics, pinnedCurrency}) => {
  const [notesOpen, setNotesOpen] = useState(false);
  if (!metrics) return null;

  const {window: applied, generatedAt} = metrics;
  const turnover = headlineMoney(metrics.flow?.turnover, pinnedCurrency);
  const charged = headlineMoney(metrics.fees?.charged, pinnedCurrency);
  const inFlight = metrics.flow?.inFlight?.orderCount ?? 0;
  const oldest = metrics.settlement?.oldestUnsettledMs;
  const wait = settlementWait(oldest, inFlight);
  // Only flag a wait we can actually vouch for.
  const settlementTone =
    wait.known && oldest > 7 * 24 * 3600 * 1000 ? 'Warn' : null;
  const fillRate = metrics.quality?.fillRate;

  const range =
    applied?.startDate || applied?.endDate
      ? `${
          applied.startDate
            ? moment(applied.startDate).format('D MMM')
            : 'the beginning'
        } – ${
          applied.endDate ? moment(applied.endDate).format('D MMM YYYY') : 'now'
        }`
      : 'all time';

  const notes = applied?.notes ?? [];

  return (
    <div className={styles.hero}>
      <div className={styles.heroTop}>
        <div className={styles.heroPrimary}>
          <span className={styles.heroLabel}>Orders in scope</span>
          <span className={styles.heroValue}>{number(metrics.orderCount)}</span>
          <span className={styles.heroSub}>
            {DATE_FIELD_LABELS[applied?.dateField] ?? 'created'} {range}
          </span>
        </div>

        <div className={styles.heroKpis}>
          <Kpi
            label="Turnover"
            value={turnover.value}
            currency={turnover.currency}
            extra={turnover.extra}
            hint="traded value"
          />
          <Kpi
            label="Fees charged"
            value={charged.value}
            currency={charged.currency}
            extra={charged.extra}
            hint="revenue"
          />
          <Kpi
            label="Awaiting settlement"
            value={number(inFlight)}
            hint={wait.text}
            tone={settlementTone}
          />
          <Kpi
            label="Fill rate"
            value={fillRate == null ? '—' : `${(fillRate * 100).toFixed(1)}%`}
            hint="of units requested"
          />
        </div>
      </div>

      {/* The caveats stay one click away rather than opening the page. */}
      {notes.length > 0 && (
        <div className={styles.notes}>
          <button
            type="button"
            className={styles.notesToggle}
            aria-expanded={notesOpen}
            onClick={() => setNotesOpen(open => !open)}>
            <span className={styles.notesBadge}>{notes.length}</span>
            What these figures do and do not include
            <svg
              className={`${styles.notesChevron} ${
                notesOpen ? styles.notesChevronOpen : ''
              }`}
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true">
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>
          {generatedAt && (
            <span className={styles.notesGenerated}>
              as of {moment(generatedAt).format('D MMM, HH:mm')}
            </span>
          )}
          <AnimatePresence initial={false}>
            {notesOpen && (
              <motion.div
                className={styles.notesReveal}
                initial={{height: 0, opacity: 0}}
                animate={{height: 'auto', opacity: 1}}
                exit={{height: 0, opacity: 0}}
                transition={{duration: 0.22, ease: 'easeOut'}}>
                <ul className={styles.notesList}>
                  {notes.map(note => (
                    <li key={note}>{note}</li>
                  ))}
                </ul>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
};

export default MetricsHero;
